import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Search, UserPlus, Mail, Phone, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { getUsers } from '../../../utils/firebase/collections';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { cn } from '../../ui/utils';

interface CompanyUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
    lastLogin?: any;
    phoneNumber?: string;
}

interface CompanyUsersDialogProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: string; // This is the company slug usually
    companyName: string;
}

export function CompanyUsersDialog({ isOpen, onClose, companyId, companyName }: CompanyUsersDialogProps) {
    const [users, setUsers] = useState<CompanyUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isOpen && companyId) {
            loadUsers();
        }
    }, [isOpen, companyId]);

    const loadUsers = async () => {
        setIsLoading(true);
        try {
            const fetchedUsers = await getUsers(companyId);
            setUsers(fetchedUsers.map((u: any) => ({
                id: u.id,
                firstName: u.firstName,
                lastName: u.lastName,
                email: u.email,
                role: u.role,
                status: u.status,
                lastLogin: u.lastLogin,
                phoneNumber: u.phoneNumber
            })));
        } catch (error) {
            console.error('Failed to load users:', error);
            // Don't toast error on 404/empty, just show empty list
        } finally {
            setIsLoading(false);
        }
    };

    const filteredUsers = users.filter(u =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[800px] max-h-[80vh] flex flex-col">
                <DialogHeader className="pb-4 border-b">
                    <DialogTitle>Users for {companyName}</DialogTitle>
                    <DialogDescription>
                        Manage user accounts and permissions for this tenant.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-4 flex-1 overflow-hidden flex flex-col">
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                            <Input
                                placeholder="Search users..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        {/* <Button className="shrink-0 bg-blue-600">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add User
                        </Button> */}
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-[300px] space-y-2 pr-1">
                        {isLoading ? (
                            <div className="text-center py-10 text-muted-foreground">Loading users...</div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground">
                                No users found for this company.
                            </div>
                        ) : (
                            filteredUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-9 w-9">
                                            <AvatarFallback className="bg-blue-100 text-blue-700 font-medium">
                                                {user.firstName?.[0]}{user.lastName?.[0]}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <div className="font-medium text-sm flex items-center gap-2">
                                                {user.firstName} {user.lastName}
                                                {user.role === 'admin' && (
                                                    <Badge variant="secondary" className="text-[10px] h-4 px-1 bg-amber-100 text-amber-700">Admin</Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                                                <span className="flex items-center"><Mail className="w-3 h-3 mr-1" /> {user.email}</span>
                                                {user.phoneNumber && (
                                                    <span className="flex items-center"><Phone className="w-3 h-3 mr-1" /> {user.phoneNumber}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            variant="outline"
                                            className={cn(
                                                "capitalize",
                                                user.status === 'active' ? "text-green-600 border-green-200 bg-green-50" : "text-slate-500 bg-slate-100"
                                            )}
                                        >
                                            {user.status}
                                        </Badge>
                                        {/* <Button variant="ghost" size="sm">Edit</Button> */}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
